/**
 * Invite Admin modal — POSTed to /api/admin/team/invitations.
 *
 * Flow: super_admin enters email + name + picks a bundle → submits → backend
 * creates admin_invitations row + sends invite email. The success state shows
 * the invite URL with a copy-to-clipboard so the inviter can hand-deliver if
 * email is delayed.
 */

import React, { useState } from 'react';
import { CheckCircle2, Copy, Mail, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';

interface Bundle {
  name: string;
  description: string;
  role: string;
  capability_count: number;
  capabilities: string[];
}

interface InviteResponse {
  success: boolean;
  data: {
    invitation_id: string;
    invite_url: string;
    expires_at: string;
    email_sent: boolean;
  };
}

export const InviteAdminModal: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [bundleName, setBundleName] = useState('Marketing Lead');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InviteResponse['data'] | null>(null);
  const [urlCopied, setUrlCopied] = useState(false);

  const { data: bundlesData } = useQuery<{ data: Bundle[] }>({
    queryKey: ['admin-team-bundles'],
    queryFn: () => adminFetch('/api/admin/team/bundles'),
    enabled: open,
  });
  const bundles = bundlesData?.data ?? [];

  const invite = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      adminFetch<InviteResponse>('/api/admin/team/invitations', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: r => {
      setResult(r.data);
      qc.invalidateQueries({ queryKey: ['admin-team-pending'] });
      qc.invalidateQueries({ queryKey: ['admin-team'] });
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Failed to send invitation';
      setError(msg);
    },
  });

  if (!open) return null;

  const selectedBundle = bundles.find(b => b.name === bundleName);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    invite.mutate({
      email: email.trim(),
      full_name: fullName.trim() || null,
      bundle_name: bundleName,
      notes: notes.trim() || null,
    });
  };

  const reset = () => {
    setEmail(''); setFullName(''); setNotes('');
    setBundleName('Marketing Lead');
    setError(null); setResult(null); setUrlCopied(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-bold text-gray-900">
              <Mail className="h-4 w-4" style={{ color: 'var(--cedar-primary)' }} />
              {result ? 'Invitation sent' : 'Invite a new admin'}
            </h2>
            <p className="mt-0.5 text-[11px] text-gray-500">
              {result ? 'Share the link below if the email doesn\'t arrive.' : 'They\'ll get an email with a single-use link to set their password.'}
            </p>
          </div>
          <button onClick={() => { reset(); onClose(); }} className="text-gray-400 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        {result ? (
          <div className="px-6 py-5 space-y-4">
            <div className="flex items-start gap-3 rounded-md bg-green-50 p-3">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--cedar-primary)' }} />
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  Invitation created
                </div>
                <div className="text-[11px] text-gray-600">
                  {result.email_sent ? 'Email sent successfully.' : 'Email failed — share the URL below directly.'}
                  {' '}Expires {new Date(result.expires_at).toLocaleDateString()}.
                </div>
              </div>
            </div>

            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Invite URL (single-use)
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  readOnly
                  value={result.invite_url}
                  className="flex-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] font-mono text-gray-700"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(result.invite_url);
                    setUrlCopied(true);
                    setTimeout(() => setUrlCopied(false), 2000);
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Copy className="h-3 w-3" />
                  {urlCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => reset()}
                className="rounded-md border border-gray-200 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Invite another
              </button>
              <button
                onClick={() => { reset(); onClose(); }}
                className="rounded-md px-4 py-2 text-xs font-medium text-white"
                style={{ backgroundColor: 'var(--cedar-primary)' }}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email" required>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="matt@coachcedar.com"
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                />
              </Field>
              <Field label="Full name (optional)">
                <input
                  type="text"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="Matt Roussel"
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                />
              </Field>
            </div>

            <Field label="Bundle" required>
              <select
                value={bundleName}
                onChange={e => setBundleName(e.target.value)}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              >
                {bundles.map(b => (
                  <option key={b.name} value={b.name}>
                    {b.name} ({b.role}, {b.capability_count} caps)
                  </option>
                ))}
              </select>
              {selectedBundle && (
                <p className="mt-1 text-[11px] italic text-gray-500">
                  {selectedBundle.description}
                </p>
              )}
            </Field>

            <Field label="Personal note (optional, included in invite email)">
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                placeholder="Welcome aboard! Ping me once you're set up."
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              />
            </Field>

            {error && (
              <div className="rounded-md bg-red-50 p-3 text-xs text-red-700">{error}</div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => { reset(); onClose(); }}
                className="rounded-md border border-gray-200 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={invite.isPending || !email.trim()}
                className="rounded-md px-4 py-2 text-xs font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: 'var(--cedar-primary)' }}
              >
                {invite.isPending ? 'Sending…' : 'Send invitation'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <label className="block">
    <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
      {label}{required && <span className="ml-0.5 text-red-500">*</span>}
    </span>
    {children}
  </label>
);

export default InviteAdminModal;
